<template>
    <div v-if="alert" class="alert-card mb-3" :class="`alert-card--${alert.riskLevel}`">

        <!-- Header -->
        <div class="d-flex align-center mb-3">
            <v-icon small class="mr-2 alert-card__icon">mdi-alert-circle-outline</v-icon>
            <span class="alert-card__title">{{ $t('ai.alert_title') }}</span>
            <ai-beta-chip class="ml-2" />
            <v-spacer />
            <v-chip x-small class="ml-2 risk-chip">
                {{ $t(`ai.risk_${alert.riskLevel}`) }}
            </v-chip>
        </div>

        <!-- Resumen -->
        <p class="alert-card__text mb-3">{{ alert.summary }}</p>

        <v-divider class="mb-3 alert-card__divider" />

        <!-- Señales -->
        <div class="alert-card__section mb-2">{{ $t('ai.alert_signals') }}</div>
        <div
            v-for="(signal, i) in alert.signals"
            :key="i"
            class="d-flex align-start mb-2"
        >
            <v-icon x-small class="mr-2 mt-1" :class="signal.type === 'wellness' ? 'signal--wellness' : 'signal--other'">
                {{ signal.type === 'wellness' ? 'mdi-heart-pulse' : 'mdi-calendar-remove' }}
            </v-icon>
            <span class="alert-card__text">{{ signal.description }}</span>
        </div>

        <ai-assumed-fields-note :fields="alert.assumedFields" />
        <ai-beta-note />

        <!-- Footer -->
        <div class="d-flex justify-end mt-3">
            <span class="alert-card__meta">
                {{ $t('ai.alert_week') }} {{ alert.week }} ·
                {{ $t('ai.rec_generated') }} {{ generatedDate }}
            </span>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { loadStudentAlert, markAlertRead } from '@/utils/AiAlerts'
import AiAssumedFieldsNote from '@/components/coach/ai/AiAssumedFieldsNote'
import AiBetaChip from '@/components/coach/ai/AiBetaChip'
import AiBetaNote from '@/components/coach/ai/AiBetaNote'

export default {
    name: 'AiAlertCard',

    components: { AiAssumedFieldsNote, AiBetaChip, AiBetaNote },

    props: {
        studentId: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            alert: null,
        }
    },

    computed: {
        ...mapState(['currentUser']),
        coachId() {
            return this.currentUser?.uid
        },
        riskColor() {
            const colors = { high: '#E53935', medium: '#FB8C00', low: '#FDD835' }
            return colors[this.alert?.riskLevel] || 'grey'
        },
        generatedDate() {
            if (!this.alert?.generatedAt) return ''
            const ts = this.alert.generatedAt
            const date = ts?.toDate ? ts.toDate() : new Date(ts)
            return this.$moment(date).format('DD/MM/YYYY')
        },
    },

    async mounted() {
        await this.load()
    },

    methods: {
        async load() {
            if (!this.coachId || !this.studentId) return
            try {
                this.alert = await loadStudentAlert(this.coachId, this.studentId)
                if (this.alert && !this.alert.read) {
                    await markAlertRead(this.alert.id)
                }
            } catch (e) {
                console.warn('[AiAlertCard] Error cargando alerta:', e?.message)
            }
        },

        signalColor(type) {
            return type === 'wellness' ? '#E53935' : '#FB8C00'
        },
    },
}
</script>

<style scoped>
.alert-card {
    padding: 14px 18px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-left: 4px solid var(--v-outline2-base);
    border-radius: 14px;
    overflow: hidden;
}

.alert-card--high {
    border-left-color: var(--v-error-base);
    background: linear-gradient(rgba(232, 92, 92, .08), rgba(232, 92, 92, .08)), var(--v-surface-base);
}

.alert-card--medium {
    border-left-color: #F59E0B;
    background: linear-gradient(rgba(245, 158, 11, .08), rgba(245, 158, 11, .08)), var(--v-surface-base);
}

.alert-card--low {
    border-left-color: var(--v-success-base);
    background: linear-gradient(rgba(0, 153, 102, .08), rgba(0, 153, 102, .08)), var(--v-surface-base);
}

.alert-card__icon {
    color: var(--v-txtSecondary-base) !important;
}

.alert-card--high .alert-card__icon {
    color: var(--v-error-base) !important;
}

.alert-card--medium .alert-card__icon {
    color: #F59E0B !important;
}

.alert-card--low .alert-card__icon {
    color: var(--v-success-base) !important;
}

.alert-card__title {
    font-size: var(--fs-section);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.alert-card__section {
    font-size: var(--fs-body);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.alert-card__text {
    font-size: var(--fs-body);
    color: var(--v-txtPrimary-base);
}

.alert-card__meta {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.alert-card__divider {
    border-color: var(--v-outline-base) !important;
}

.signal--wellness {
    color: var(--v-error-base) !important;
}

.signal--other {
    color: #F59E0B !important;
}

.risk-chip {
    height: 20px !important;
    border-radius: 6px !important;
    font-size: 11px !important;
    font-weight: 600;
    border: 1px solid transparent;
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
}

.alert-card--high .risk-chip {
    background: rgba(232, 92, 92, .16) !important;
    border-color: rgba(232, 92, 92, .35);
    color: var(--v-error-base) !important;
}

.alert-card--medium .risk-chip {
    background: rgba(245, 158, 11, .16) !important;
    border-color: rgba(245, 158, 11, .35);
    color: #F59E0B !important;
}

.alert-card--low .risk-chip {
    background: rgba(0, 153, 102, .16) !important;
    border-color: rgba(0, 153, 102, .35);
    color: var(--v-success-base) !important;
}

.theme--light .alert-card--medium .risk-chip,
.theme--light .alert-card--medium .alert-card__icon {
    color: #B45309 !important;
}
</style>
